import React from 'react';
import { ArrowUpRightIcon, ArrowDownRightIcon, CreditCardIcon, BanknoteIcon, ReceiptIcon, CheckCircleIcon, StoreIcon, UserPlusIcon } from 'lucide-react';
import Card from '../ui/Card';
import { DashboardFilters } from '../../pages/Dashboard';
const baseKpiData = [{
  id: 'volume',
  label: 'Total Transaction Volume',
  baseValue: 85432,
  trend: '+12.5%',
  isPositive: true,
  icon: CreditCardIcon,
  color: 'text-primary-600 dark:text-primary-400',
  bg: 'bg-primary-100 dark:bg-primary-900/40',
  cardBg: 'bg-gradient-to-br from-white to-primary-50/30 dark:from-slate-800 dark:to-primary-900/10'
}, {
  id: 'value',
  label: 'Total Transaction Value',
  baseValue: 12.57,
  unit: 'B',
  prefix: 'PKR ',
  trend: '+8.7%',
  isPositive: true,
  icon: BanknoteIcon,
  color: 'text-accent-600 dark:text-accent-400',
  bg: 'bg-accent-100 dark:bg-accent-900/40',
  cardBg: 'bg-gradient-to-br from-white to-accent-50/30 dark:from-slate-800 dark:to-accent-900/10'
}, {
  id: 'ticket',
  label: 'Average Ticket Size',
  baseValue: 14711,
  prefix: 'PKR ',
  trend: '+3.2%',
  isPositive: true,
  icon: ReceiptIcon,
  color: 'text-violet-600 dark:text-violet-400',
  bg: 'bg-violet-100 dark:bg-violet-900/40',
  cardBg: 'bg-gradient-to-br from-white to-violet-50/30 dark:from-slate-800 dark:to-violet-900/10'
}, {
  id: 'success',
  label: 'Transaction Success Rate',
  baseValue: 98.7,
  suffix: '%',
  trend: '+0.3%',
  isPositive: true,
  icon: CheckCircleIcon,
  color: 'text-emerald-600 dark:text-emerald-400',
  bg: 'bg-emerald-100 dark:bg-emerald-900/40',
  cardBg: 'bg-gradient-to-br from-white to-emerald-50/30 dark:from-slate-800 dark:to-emerald-900/10'
}, {
  id: 'active_merchants',
  label: 'Active Merchants Today',
  baseValue: 4890,
  trend: '+5.1%',
  isPositive: true,
  icon: StoreIcon,
  color: 'text-amber-600 dark:text-amber-400',
  bg: 'bg-amber-100 dark:bg-amber-900/40',
  cardBg: 'bg-gradient-to-br from-white to-amber-50/30 dark:from-slate-800 dark:to-amber-900/10'
}, {
  id: 'new_merchants',
  label: 'New Merchants Onboarded',
  baseValue: 124,
  trend: '+15.8%',
  isPositive: true,
  icon: UserPlusIcon,
  color: 'text-sky-600 dark:text-sky-400',
  bg: 'bg-sky-100 dark:bg-sky-900/40',
  cardBg: 'bg-gradient-to-br from-white to-sky-50/30 dark:from-slate-800 dark:to-sky-900/10'
}];
const timeMultipliers = {
  Today: 1,
  Week: 7,
  MTD: 22,
  YTD: 265
};
const schemeMultipliers: Record<string, number> = {
  'All Schemes': 1,
  Visa: 0.35,
  MasterCard: 0.28,
  PayPak: 0.2,
  UnionPay: 0.1,
  RAAST: 0.07
};
const channelMultipliers: Record<string, number> = {
  'All Channels': 1,
  POS: 0.45,
  'E-commerce': 0.35,
  'RAAST QR': 0.2
};
interface DashboardKPIsProps {
  filters: DashboardFilters;
}
export const DashboardKPIs: React.FC<DashboardKPIsProps> = ({
  filters
}) => {
  const {
    timeRange,
    scheme,
    channel
  } = filters;
  const getFilteredValue = (baseValue: number, kpiId: string) => {
    const timeMult = timeMultipliers[timeRange];
    const schemeMult = schemeMultipliers[scheme];
    const channelMult = channelMultipliers[channel];
    if (kpiId === 'success') {
      const variation = (schemeMult + channelMult) / 2;
      return Math.min(99.9, baseValue * (0.98 + variation * 0.02));
    }
    if (kpiId === 'ticket') {
      return baseValue * (0.9 + schemeMult * 0.2) * (0.95 + channelMult * 0.1);
    }
    return baseValue * timeMult * schemeMult * channelMult;
  };
  const formatValue = (value: number, kpi: (typeof baseKpiData)[0]) => {
    if (kpi.id === 'success') return `${value.toFixed(1)}%`;
    if (kpi.id === 'value') {
      if (value >= 1) return `PKR ${value.toFixed(2)}B`;
      return `PKR ${(value * 1000).toFixed(0)}M`;
    }
    if (kpi.id === 'ticket') return `PKR ${Math.round(value).toLocaleString()}`;
    return Math.round(value).toLocaleString();
  };
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6" data-id="element-163">
      {baseKpiData.map(kpi => {
      const Icon = kpi.icon;
      const filteredValue = getFilteredValue(kpi.baseValue, kpi.id);
      const displayValue = formatValue(filteredValue, kpi);
      return <div key={kpi.id} className={`flex flex-col justify-between p-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-slate-100 dark:border-slate-700/50 ${kpi.cardBg} relative overflow-hidden group`} data-id="element-164">
            {/* Decorative background blur */}
            <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-20 blur-2xl group-hover:opacity-40 transition-opacity duration-500 ${kpi.bg}`} data-id="element-165"></div>

            <div className="flex justify-between items-start mb-6 relative z-10" data-id="element-166">
              <div className={`p-3 rounded-xl ${kpi.bg} ring-1 ring-white/50 dark:ring-black/20 shadow-sm`} data-id="element-167">
                <Icon className={`h-6 w-6 ${kpi.color}`} strokeWidth={2.5} data-id="element-168" />
              </div>
              <div className={`flex items-center text-xs font-bold px-2.5 py-1 rounded-full shadow-sm border ${kpi.isPositive ? 'text-emerald-700 bg-emerald-50 border-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/30 dark:border-emerald-800/50' : 'text-rose-700 bg-rose-50 border-rose-100 dark:text-rose-400 dark:bg-rose-900/30 dark:border-rose-800/50'}`} data-id="element-169">
                {kpi.isPositive ? <ArrowUpRightIcon className="h-3.5 w-3.5 mr-1" strokeWidth={3} data-id="element-170" /> : <ArrowDownRightIcon className="h-3.5 w-3.5 mr-1" strokeWidth={3} data-id="element-171" />}
                {kpi.trend}
              </div>
            </div>
            <div className="relative z-10" data-id="element-172">
              <h3 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight mb-1.5" data-id="element-173">
                {displayValue}
              </h3>
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400" data-id="element-174">
                {kpi.label}
              </p>
            </div>
          </div>;
    })}
    </div>;
};